import { useState, useMemo } from 'react'
import { Container, H4, H3, P1, P2, P3, useWindowSize } from '@moneymade/moneymade-ui'
import PropTypes from 'prop-types'
import { useRouter } from 'next/router'
import { getCdnImg } from 'utils/getters'
import ImgLazy from 'components/ui/ImgLazy/ImgLazy'

import { getGraphData } from 'modules/home/HomePage/homePageUtils'
import { toMoneyFormat } from 'utils'
import FlashingBlock from 'components/ui/FlashingBlock/FlashingBlock'
import { TIMELINES_CONVERTER } from 'modules/asset/AssetPage/assetPageConstants'
import { getParsedCompareData, toFilteredGraphData } from 'modules/asset/AssetPage/assetPageUtils'
import Graph from 'modules/discover/PlatformPageNew/Snapshot/Graph/Graph'
import AccreditedText from 'modules/discover/PlatformPageNew/Snapshot/AccreditedText/AccreditedText'
import Team from 'modules/discover/PlatformPageNew/Snapshot/Team/Team'
import EarnText from 'modules/discover/PlatformPageNew/Snapshot/EarnText/EarnText'
import GraphStatic from 'modules/discover/PlatformPageNew/Snapshot/GraphStatic/GraphStatic'
import styles from './Snapshot.module.scss'

const Snapshot = ({
  urlSlug,
  title,
  dataSlug,
  targetReturn,
  targetReturnText,
  liquidity,
  liquidityText,
  team,
  teamText,
  investFrom,
  investIn,
  howYouEarn,
  titleGraph,
  teamImgs,
  investors,
  assetCompare,
  industryChange
}) => {
  const { screenWidth } = useWindowSize()
  const { query } = useRouter()

  const openToAllInvestorsModalText =
    "<p>a) An individual with gross income exceeding $200,000 in each of the two most recent years or joint income with a spouse or partner exceeding $300,000 for those years and a reasonable expectation of the same income level in the current year.<br><br>b) A person whose individual net worth, or joint net worth with that person's spouse or partner, exceeds $1,000,000, excluding the person's primary residence.</p>"
  const howYouEarnModalText =
    '<p><strong>Growth investing</strong> is a type of investment strategy that concentrates on increasing the value of the financial assets one has, i.e. capital appreciation rather than annual income or dividends.</p><p><strong>Income investing </strong>involves building a portfolio using dividend-paying assets designed to generate cash on a recurring basis. The objective of income investments is to generate a constant stream of income. form of dividends, bond yields, and interest payments.</p>'
  const compareData = useMemo(() => getParsedCompareData(assetCompare, dataSlug, 'spx'), [assetCompare, dataSlug])

  const pricesData = useMemo(() => {
    const filteredData = toFilteredGraphData(TIMELINES_CONVERTER['10Y'], compareData)
    const { rangeData } = filteredData

    const priceSlug1First = rangeData?.[0]?.priceSlug1
    const priceSlug1Last = rangeData?.[(rangeData || []).length - 1]?.priceSlug1

    return {
      slugFirst: ((priceSlug1Last - priceSlug1First) / priceSlug1First) * 100
    }
  }, [compareData])

  const [prevSumReturnSlug1, setPrevSumReturnSlug1] = useState(0)
  const [graphFocus, setGraphFocus] = useState(false)

  const handleSetPrevSum = returnSlug1 => {
    setPrevSumReturnSlug1(returnSlug1)
  }

  const targetText = useMemo(
    () => [
      {
        value: targetReturn,
        title: 'Target Return',
        description: targetReturnText
      },
      {
        value: liquidity,
        title: 'Liquidity',
        description: liquidityText
      }
    ],
    [targetReturn, targetReturnText, liquidity, liquidityText]
  )

  const earnTexts = useMemo(
    () => [
      {
        title: 'How You Earn',
        value: howYouEarn,
        hoverText: howYouEarnModalText
      },
      {
        title: 'Invest From',
        value: investFrom,
        hoverText: ''
      },
      {
        title: 'Invest in',
        value: investIn,
        hoverText: ''
      }
    ],
    [howYouEarn, investFrom, investIn]
  )

  const graphStatic = useMemo(() => (industryChange ? getGraphData() : []), [industryChange])

  return (
    <div className={styles.Snapshot}>
      <Container width={1230} className={styles.Content}>
        <div className={styles.Header}>
          <H3 weight="semibold" id={urlSlug}>
            {title}
          </H3>

          <div className={styles.AccreditedTop}>
            <AccreditedText
              investors={investors}
              text={
                investors === 'All'
                  ? '<p>Open to all investor types with no restrictions, accessible to anyone regardless of accreditation status.</p>'
                  : openToAllInvestorsModalText
              }
            />
          </div>
        </div>

        <div className={styles.General}>
          <div className={styles.Left}>
            {query?.slug === 'masterworks' ? (
              <ImgLazy
                className={styles.Image}
                src={getCdnImg('https://assets.moneymade.io/images/discover/masterworks.jpg', 570)}
                srcSet={getCdnImg('https://assets.moneymade.io/images/discover/masterworks.jpg', 570, true)}
                alt="post-img"
                loading="lazy"
              />
            ) : (
              <div className={styles.Graph}>
                <div className={styles.TitleSection}>
                  <P1>{titleGraph}</P1>

                  <FlashingBlock>
                    {!industryChange ? (
                      <H4
                        className={`${styles.Title} ${
                          (graphFocus ? prevSumReturnSlug1 : pricesData?.slugFirst) >= 0 ? 'color-green' : 'color-red'
                        }`}
                        weight="semibold"
                      >
                        {`${(graphFocus ? prevSumReturnSlug1 : pricesData?.slugFirst) > 0 ? '+' : ''}${toMoneyFormat(
                          (graphFocus ? prevSumReturnSlug1 : pricesData?.slugFirst)?.toFixed(1) || 0,
                          2
                        )}%`}
                      </H4>
                    ) : (
                      <H4 className={`${styles.Title} ${'color-green'}`} weight="semibold">
                        {industryChange}
                      </H4>
                    )}
                  </FlashingBlock>
                </div>

                <div className={styles.GraphSection}>
                  {!industryChange ? (
                    <Graph
                      chartTimeline="10Y"
                      data={toFilteredGraphData(TIMELINES_CONVERTER['10Y'], compareData)}
                      pricesData={pricesData}
                      slugs={{ slugFirst: dataSlug }}
                      slugCurrency="$"
                      handleSetPrevSum={handleSetPrevSum}
                      handleGraphFocus={setGraphFocus}
                    />
                  ) : (
                    <div className={styles.GraphStatic}>
                      <GraphStatic graph={graphStatic} />
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className={styles.TeamLeft}>
              <Team team={team} teamText={teamText} teamImgs={teamImgs} />
            </div>
          </div>

          <div className={styles.Right}>
            <div className={styles.Table}>
              {targetText.map(({ value, title: mainTitle, description }) => (
                <div className={styles.TableItem} key={value}>
                  <H3 weight="semibold" className="color-green">
                    {value}
                  </H3>

                  <P2 weight="normal">{mainTitle}</P2>

                  <P3 className={styles.Desc}>{description}</P3>
                </div>
              ))}

              <div className={styles.TeamTop}>
                <Team team={team} teamText={teamText} teamImgs={teamImgs} />
              </div>
            </div>

            <div className={styles.EarnSection}>
              <div className={styles.Top}>
                {earnTexts.slice(0, screenWidth >= 961 ? 3 : 2)?.map(earnText => (
                  <EarnText key={earnText.title} {...earnText} />
                ))}
              </div>

              {screenWidth < 961 ? (
                <div className={styles.Bottom}>
                  <EarnText {...earnTexts[2]} />
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className={styles.TeamBottom}>
          <Team team={team} teamText={teamText} teamImgs={teamImgs} />
        </div>

        <div className={styles.AccreditedBottom}>
          <AccreditedText investors={investors} text={openToAllInvestorsModalText} />
        </div>
      </Container>
    </div>
  )
}

Snapshot.propTypes = {
  title: PropTypes.string,
  urlSlug: PropTypes.string,
  dataSlug: PropTypes.string,
  targetReturn: PropTypes.string,
  targetReturnText: PropTypes.string,
  liquidity: PropTypes.string,
  liquidityText: PropTypes.string,
  team: PropTypes.string,
  teamText: PropTypes.string,
  investFrom: PropTypes.number,
  investIn: PropTypes.string,
  howYouEarn: PropTypes.string,
  titleGraph: PropTypes.string,
  teamImgs: PropTypes.arrayOf(PropTypes.string),
  investors: PropTypes.string,
  assetCompare: PropTypes.shape({
    tenYearsPeriod: PropTypes.shape({
      spx: PropTypes.arrayOf(
        PropTypes.shape({
          date: PropTypes.string
        })
      )
    })
  }),
  industryChange: PropTypes.string
}

Snapshot.defaultProps = {
  title: '',
  urlSlug: '',
  dataSlug: '',
  targetReturn: '',
  targetReturnText: '',
  liquidity: '',
  liquidityText: '',
  team: '',
  teamText: '',
  investFrom: 0,
  investIn: '',
  howYouEarn: '',
  titleGraph: '',
  teamImgs: [],
  investors: '',
  assetCompare: {},
  industryChange: ''
}

export default Snapshot
